import React, { useState, useEffect, useRef } from 'react';
import { Typography, Box, Button, Paper } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import LanguageForm from '../forms/LanguageForm';
import useLanguages from '../../hooks/useLanguages';
import useProfile from '../../hooks/UseProfile';
import DeleteDialog from 'core/components/shared/DeleteDialog';
import LanguageList from './LanguageList';
import { useQueryClient } from 'react-query';

const proficiencyColors = {
    'Native': 'success',
    'Fluent': 'primary',
    'Advanced': 'info',
    'Intermediate': 'warning',
    'Basic': 'default'
};

const LanguageSection = ({ cv, compact = false, onAddClick, triggerAdd, readonly = false }) => {
    const queryClient = useQueryClient();
    const { languages, addLanguage, updateLanguage, deleteLanguage, isLoading } = useLanguages(cv?.id);
    const { refreshProfile } = useProfile();
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const prevTriggerRef = useRef(0);

    const handleAdd = () => {
        setSelectedLanguage(null);
        setIsModalOpen(true);
    };

    useEffect(() => {
        if (triggerAdd && triggerAdd !== prevTriggerRef.current) {
            prevTriggerRef.current = triggerAdd;
            handleAdd();
        }
    }, [triggerAdd]);

    const handleEdit = (language) => {
        setSelectedLanguage(language);
        setIsModalOpen(true);
    };

    const handleDelete = (language) => {
        setSelectedLanguage(language);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        await queryClient.invalidateQueries(['languages', cv?.id || null]);
        await queryClient.refetchQueries(['languages', cv?.id || null]);
        await queryClient.invalidateQueries(['languages']);
        setIsDeleteModalOpen(false);
    };

    const content = (
        <>
            {error && (
                <Typography color="error" className="mb-4">
                    {error}
                </Typography>
            )}

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={3}>
                    <Typography>Loading languages...</Typography>
                </Box>
            ) : (
                <LanguageList 
                    languages={languages} 
                    onEdit={readonly ? undefined : handleEdit} 
                    onDelete={readonly ? undefined : handleDelete}
                    compact={compact}
                />
            )}

            <CustomModal
                open={isModalOpen}
                setOpen={setIsModalOpen}
                title={selectedLanguage ? 'Edit Language' : 'Add New Language'}
            >
                <LanguageForm 
                    id={selectedLanguage?.id}
                    onClose={() => setIsModalOpen(false)}
                    cvId={cv?.id}
                />
            </CustomModal>

            <DeleteDialog
                url={`/job/cv/userlanguage/${selectedLanguage?.id}`}
                isOpen={isDeleteModalOpen}
                setOpen={setIsDeleteModalOpen}
                notifyDone={handleConfirmDelete}
                message="Are you sure you want to delete this language?"
            />
        </>
    );

    if (compact) {
        return content;
    }

    return (
        <Paper elevation={0} sx={{ p: 2.5, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Languages
                </Typography>
                {!readonly && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                )}
            </Box>
            {content}
        </Paper>
    );
};

export default LanguageSection; 